class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 500);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    document.getElementById('ProductGridContainer').querySelector('.collection').classList.add('loading');
    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
        if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
        FacetFiltersForm.scrollToProducts();
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
    FacetFiltersForm.scrollToProducts();
  }

  static renderProductGridContainer(html) {
    document.getElementById('ProductGridContainer').innerHTML = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('ProductGridContainer').innerHTML;

    document
      .getElementById('ProductGridContainer')
      .querySelectorAll('.scroll-trigger')
      .forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
      });

    FacetFiltersForm.pushSoldOutToBottom();
  }

  static scrollToProducts() {
    const productGrid = document.getElementById('ProductGridContainer');
    if (productGrid) {
      /* Small delay to let the DOM update settle before scrolling */
      requestAnimationFrame(() => {
        productGrid.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }
  }

  static pushSoldOutToBottom() {
    const grid = document.getElementById('product-grid');
    if (!grid) return;

    const items = Array.from(grid.children);
    const inStock = [];
    const soldOut = [];

    items.forEach(item => {
      // Check data-available attribute first
      if (item.dataset.available === 'false') {
        soldOut.push(item);
      } else if (item.dataset.available === 'true') {
        inStock.push(item);
      } else {
        // Fallback: detect sold-out badge text inside the card
        const badge = item.querySelector('.badge');
        const isSoldOut = badge && badge.textContent.trim().toLowerCase().includes('sold out');
        if (isSoldOut) {
          soldOut.push(item);
        } else {
          inStock.push(item);
        }
      }
    });

    // Only reorder if there are sold-out items mixed in
    if (soldOut.length === 0) return;

    // Append in-stock first, then sold-out
    inStock.forEach(item => grid.appendChild(item));
    soldOut.forEach(item => grid.appendChild(item));
  }

  static renderProductCount(html) {
    const count = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductCount').innerHTML;
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    container.innerHTML = count;
    container.classList.remove('loading');
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');

    const facetDetailsElements = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );
    const matchesIndex = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.dataset.index === jsFilter.dataset.index : false;
    };
    const facetsToRender = Array.from(facetDetailsElements).filter((element) => !matchesIndex(element));
    const countsToRender = Array.from(facetDetailsElements).find(matchesIndex);

    facetsToRender.forEach((element) => {
      document.querySelector(`.js-filter[data-index="${element.dataset.index}"]`).innerHTML = element.innerHTML;
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });

    document.getElementById('FacetFiltersFormMobile').closest('menu-drawer').bindEvents();
  }

  static renderCounts(source, target) {
    const targetElement = target.querySelector('.facets__selected');
    const sourceElement = source.querySelector('.facets__selected');

    const targetElementAccessibility = target.querySelector('.facets__summary');
    const sourceElementAccessibility = source.querySelector('.facets__summary');

    if (sourceElement && targetElement) {
      target.querySelector('.facets__selected').outerHTML = source.querySelector('.facets__selected').outerHTML;
    }

    if (targetElementAccessibility && sourceElementAccessibility) {
      target.querySelector('.facets__summary').outerHTML = source.querySelector('.facets__summary').outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    if (event.srcElement.className == 'mobile-facets__checkbox') {
      const searchParams = this.createSearchParams(event.target.closest('form'));
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];
      const isMobile = event.target.closest('form').id === 'FacetFiltersFormMobile';

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
            const noJsElements = document.querySelectorAll('.no-js-list');
            noJsElements.forEach((el) => el.remove());
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join('&'), event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach((element) =>
      element.addEventListener('change', this.onRangeChange.bind(this))
    );
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('min', 0);
    if (maxInput.value === '') minInput.setAttribute('max', maxInput.getAttribute('max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('min'));
    const max = Number(input.getAttribute('max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);

class StickyFilterBar extends HTMLElement {
  connectedCallback() {
    const filterBtn = this.querySelector('.collection-sticky-bar__filter-btn');
    const sortBtn = this.querySelector('.collection-sticky-bar__sort-btn');
    const closeBtn = this.querySelector('.collection-sort-drawer__close');
    const overlay = this.querySelector('.collection-sort-drawer__overlay');
    const optionBtns = this.querySelectorAll('.collection-sort-drawer__option-btn');
    
    if (filterBtn) {
      filterBtn.addEventListener('click', (e) => {
        e.preventDefault();
        const menuDrawer = document.querySelector('menu-drawer.mobile-facets__wrapper');
        if (menuDrawer) {
          const details = menuDrawer.querySelector('details');
          const summary = details.querySelector('summary');
          if (details && summary) {
            details.setAttribute('open', '');
            menuDrawer.openMenuDrawer(summary);
          }
        }
      });
    }
    
    if (sortBtn) {
      sortBtn.addEventListener('click', (e) => {
        e.preventDefault();
        this.toggleSortDrawer(true);
      });
    }
    
    if (closeBtn) {
      closeBtn.addEventListener('click', (e) => {
        e.preventDefault();
        this.toggleSortDrawer(false);
      });
    }
    
    if (overlay) {
      overlay.addEventListener('click', (e) => {
        e.preventDefault();
        this.toggleSortDrawer(false);
      });
    }
    
    optionBtns.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const val = btn.dataset.value;
        this.selectSortOption(val);
      });
    });
  }
  
  toggleSortDrawer(open) {
    const drawer = this.querySelector('.collection-sort-drawer');
    if (drawer) {
      if (open) {
        // Move active option to the top of the list
        const optionsContainer = drawer.querySelector('.collection-sort-drawer__options');
        const activeBtn = optionsContainer && optionsContainer.querySelector('.collection-sort-drawer__option-btn.is-active');
        if (optionsContainer && activeBtn) {
          optionsContainer.prepend(activeBtn);
        }
        drawer.classList.add('is-open');
        document.body.classList.add('overflow-hidden');
      } else {
        drawer.classList.remove('is-open');
        document.body.classList.remove('overflow-hidden');
      }
    }
  }
  
  selectSortOption(val) {
    // Update visual state - toggle is-active on buttons
    this.querySelectorAll('.collection-sort-drawer__option-btn').forEach(btn => {
      btn.classList.toggle('is-active', btn.dataset.value === val);
    });

    this.toggleSortDrawer(false);
    
    const sortBySelects = [
      document.getElementById('SortBy-mobile'),
      document.getElementById('SortBy')
    ];
    
    let triggered = false;
    sortBySelects.forEach(select => {
      if (select) {
        select.value = val;
        if (!triggered) {
          // FacetFiltersForm listens for 'input' event, not 'change'
          select.dispatchEvent(new Event('input', { bubbles: true }));
          select.dispatchEvent(new Event('change', { bubbles: true }));
          triggered = true;
        }
      }
    });

    // Fallback: if no select was found, navigate via URL
    if (!triggered) {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', val);
      window.location.href = url.toString();
    }
  }
}
if (!customElements.get('sticky-filter-bar')) {
  customElements.define('sticky-filter-bar', StickyFilterBar);
}

// Push sold-out products to bottom on initial page load
document.addEventListener('DOMContentLoaded', () => {
  if (typeof FacetFiltersForm !== 'undefined') {
    FacetFiltersForm.pushSoldOutToBottom();
  }
});

// Also run immediately in case DOMContentLoaded already fired
if (document.readyState !== 'loading' && typeof FacetFiltersForm !== 'undefined') {
  FacetFiltersForm.pushSoldOutToBottom();
}

// MutationObserver: watch for external scripts (like Globo Filter) reordering the grid
(function() {
  let debounceTimer = null;
  const initObserver = () => {
    const grid = document.getElementById('product-grid');
    if (!grid) return;

    const observer = new MutationObserver(() => {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        if (typeof FacetFiltersForm !== 'undefined') {
          FacetFiltersForm.pushSoldOutToBottom();
        }
      }, 300);
    });

    observer.observe(grid, { childList: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initObserver);
  } else {
    initObserver();
  }
})();
